/**
 * Блок расхождений сверки платёжных систем с дилингом
 * (карточка BL-2026-08-15-4028).
 *
 * Flow: опрос /api/treasury/recon-breaks раз в 60 секунд → таблица открытых
 *       расхождений → разбор с обязательной причиной → закрытие.
 *
 * Главное свойство блока: расхождение НЕЛЬЗЯ закрыть без причины. Требование
 * держат три слоя — форма, обработчик и CHECK схемы, — потому что закрывают
 * расхождения в том числе через psql при разборе (DEC-028).
 *
 * Зависимости:
 *   - react: состояние и жизненный цикл;
 *   - api.js: loadReconBreaks, resolveReconBreak.
 *
 * Связанные файлы:
 *   - src/App.jsx — размещает блок на экране;
 *   - docs/specs/BL-2026-08-15-4028-psp-dealing-recon.md — техспека.
 */

import { useCallback, useEffect, useState } from 'react'

import { loadReconBreaks, resolveReconBreak } from './api'

/** Период опроса. Сверка идёт раз в полчаса — чаще смотреть незачем. */
const POLL_MS = 60000

/** Человеческие названия видов расхождений. */
const KIND_TITLES = {
  psp_not_in_dealing: 'нет зачисления на дилинге',
  dealing_not_in_psp: 'зачисление без платежа',
  amount_mismatch: 'суммы разошлись',
  ambiguous_match: 'несколько кандидатов',
}

/**
 * Показывает денежную величину.
 *
 * Пустое значение — прочерк, а не ноль: у расхождения сумма есть всегда,
 * но прочерк честнее нуля, если её не передали (BR-011).
 */
export function formatBreakAmount(value) {
  if (value === null || value === undefined || value === '') return '—'
  const n = Number(value)
  if (Number.isNaN(n)) return value
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 8 })
}

/** Показывает момент в UTC: всё хранение и сравнение в проекте — в UTC. */
export function formatBreakMoment(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC'
}

/**
 * Разбор одного расхождения в строке таблицы.
 *
 * Форма раскрывается по щелчку, а в строке стоит одна короткая ссылка —
 * тот же приём, что у привязки клиента в блоке движений. Поле причины
 * и две кнопки, показанные в КАЖДОЙ строке, требуют втрое больше ширины,
 * чем есть у столбца в колоночной раскладке: столбец выходил за карточку
 * и наезжал на соседний блок (карточка BL-2026-09-11-ec35).
 *
 * Разбирают расхождения по одному, а смотрят на перечень целиком —
 * поэтому разворачивается ровно та строка, с которой работают.
 */
function BreakActions({ id, reason, onReason, onClose }) {
  const [editing, setEditing] = useState(false)

  if (!editing) {
    return (
      <button type="button" className="link" onClick={() => setEditing(true)}>
        разобрать
      </button>
    )
  }

  return (
    <div className="inline">
      <input
        type="text"
        placeholder="причина"
        value={reason}
        onChange={(e) => onReason(e.target.value)}
        aria-label={`причина разбора расхождения ${id}`}
      />
      <button type="button" onClick={() => onClose('resolved')}>
        Разобрано
      </button>
      <button type="button" onClick={() => onClose('not_applicable')}>
        Сверять нечего
      </button>
      <button type="button" className="link" onClick={() => setEditing(false)}>
        Отмена
      </button>
    </div>
  )
}

export default function ReconBreaksPanel({ user }) {
  const [report, setReport] = useState(null)
  const [error, setError] = useState('')
  const [reasons, setReasons] = useState({})
  const [formError, setFormError] = useState('')

  const load = useCallback(() => {
    loadReconBreaks(true)
      .then((data) => {
        setReport(data)
        setError('')
      })
      .catch((e) => setError(e.message))
  }, [])

  useEffect(() => {
    load()
    const timer = setInterval(load, POLL_MS)
    return () => clearInterval(timer)
  }, [load])

  if (error) {
    return (
      <section className="card card-wide">
        <h2>Расхождения сверки</h2>
        <div className="error">{error}</div>
      </section>
    )
  }

  if (!report) {
    return (
      <section className="card card-wide">
        <h2>Расхождения сверки</h2>
        <p className="muted">Загрузка…</p>
      </section>
    )
  }

  const breaks = report.breaks || []
  const canWrite = user && (user.role === 'admin' || user.role === 'backoffice')

  const close = (id, status) => {
    const reason = (reasons[id] || '').trim()
    if (!reason) {
      setFormError('Причина обязательна: расхождение без объяснения нечем оспорить через месяц.')
      return
    }
    setFormError('')
    resolveReconBreak(id, status, reason)
      .then(() => {
        setReasons({ ...reasons, [id]: '' })
        load()
      })
      .catch((e) => setFormError(e.message))
  }

  return (
    <section className="card card-wide">
      <h2>Расхождения сверки</h2>

      <div className="card-fill">
      {breaks.length === 0 ? (
        <p className="muted">
          Открытых расхождений нет. Пополнения, сторону которых система не загружает,
          в расхождения не попадают и названы отдельно — в перечне для разбора.
        </p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Что не сошлось</th>
              <th>Система</th>
              <th>Заявка</th>
              <th className="num">Сумма</th>
              <th>Валюта</th>
              <th>Момент</th>
              {canWrite && <th>Разбор</th>}
            </tr>
          </thead>
          <tbody>
            {breaks.map((b) => (
              <tr key={b.id}>
                <td>{KIND_TITLES[b.kind] || b.kind}</td>
                <td>{b.source_code || '—'}</td>
                <td>{b.request_no || '—'}</td>
                <td className="num">{formatBreakAmount(b.amount)}</td>
                <td>{b.currency_code}</td>
                <td>{formatBreakMoment(b.occurred_at)}</td>
                {canWrite && (
                  <td>
                    <BreakActions
                      id={b.id}
                      reason={reasons[b.id] || ''}
                      onReason={(v) => setReasons({ ...reasons, [b.id]: v })}
                      onClose={(status) => close(b.id, status)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      </div>

      {formError && <div className="error">{formError}</div>}
    </section>
  )
}
